/**
 * Recording on a phone (docs/design/mobile-recorder.md): the tester uses the real phone, the Device Agent watches it
 * (touches through getevent, the screen through uiautomator dumps) and sends raw actions; the web app compiles them
 * into plain-English steps with `compileMobile`. Pure functions and the session contract between API, agent and web.
 */
import { z } from 'zod';
import { recordedExchangeSchema } from './perf.js';
import type { ServerCertForAgent } from './serverCerts.js';
import type { StepKind } from './testing.js';

/** Device Agent version that can record (job `record-start`). "dev" (run from source) always can. */
export const AGENT_RECORD_MIN_VERSION = '1.2.2';
/** Device Agent version that can also record the app's network traffic (performance scripts, its recording proxy). */
export const AGENT_TRAFFIC_MIN_VERSION = '1.4.0';

/** "1.1.0.7" >= "1.1.0"; "dev" counts as new enough. */
export function agentVersionAtLeast(version: string | null | undefined, min: string): boolean {
  if (!version) return false;
  if (version === 'dev') return true;
  const a = version.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const b = min.split('.').map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d > 0;
  }
  return true;
}

/** What the agent knows about the element a touch landed on (from the uiautomator dump). */
export const uiElementSchema = z.object({
  /** android.widget.Button, android.widget.EditText… */
  cls: z.string().max(200),
  text: z.string().max(500).optional(),
  /** content-description */
  desc: z.string().max(500).optional(),
  /** resource-id without the package ("login_button"). */
  resId: z.string().max(200).optional(),
  hint: z.string().max(500).optional(),
  /** Text of its children when it has none of its own (a clickable row or card). */
  innerText: z.string().max(500).optional(),
  checkable: z.boolean().optional(),
  checked: z.boolean().optional(),
  password: z.boolean().optional(),
  editable: z.boolean().optional(),
  /** Identity within a screen (resource-id or class + bounds): tells fields apart when merging. */
  key: z.string().max(400),
});
export type UiElement = z.infer<typeof uiElementSchema>;

const ts = z.number().int().nonnegative();
const screenSchema = z.object({ w: z.number().int().positive(), h: z.number().int().positive() });

/** Id of a screenshot taken as a finger landed (agent-made; one per tap that found no name). */
const shotId = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/);

/**
 * Raw actions the agent saw. `stale`: matched against a screen dump older than the previous gesture (the tester was
 * faster than the dump), so the element may be wrong; the review flags the step. `shot`: a tap without a name came
 * with a screenshot (`recordingEventsSchema.shots`); the project's AI model names the element from it. So does a tap
 * whose element the phone reported only after the finger lifted (the app may have changed the screen by then).
 */
export const mobileRawActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('launch'), appId: z.string().max(200), ts }),
  z.object({ kind: z.literal('tap'), el: uiElementSchema.nullable(), x: z.number(), y: z.number(), screen: screenSchema, long: z.boolean().optional(), stale: z.boolean().optional(), shot: shotId.optional(), ts }),
  z.object({ kind: z.literal('type'), el: uiElementSchema, value: z.string().max(2000), ts }),
  z.object({ kind: z.literal('scroll'), direction: z.enum(['up', 'down', 'left', 'right']), el: uiElementSchema.nullable(), ts }),
  z.object({ kind: z.literal('back'), ts }),
  /** Added in the web app (Add a check), never by the agent. */
  z.object({ kind: z.literal('assert'), text: z.string().max(500), ts }),
]);
export type MobileRawAction = z.infer<typeof mobileRawActionSchema>;

export const RECORDING_STATUSES = ['starting', 'recording', 'stopped', 'failed', 'cancelled'] as const;
export type RecordingStatus = (typeof RECORDING_STATUSES)[number];

/**
 * The recording proxy's state (performance recordings): requests recorded so far, and hosts whose connections the app
 * refused because it does not trust 1QA's certificate (release builds, certificate pinning). Those hosts are passed
 * through unrecorded, so the app keeps working.
 */
export const recordingTrafficSchema = z.object({
  requests: z.number().int().nonnegative(),
  refused: z.array(z.object({ host: z.string().max(300), count: z.number().int().positive() })).max(50),
  /** Where the agent put its certificate on the phone, for installing it ("Download/1QA-recorder-CA.crt"). */
  certOnPhone: z.string().max(200).optional(),
  /** The certificate's SHA-256 fingerprint (to tell it apart from an older one in the phone's settings). */
  certSha256: z.string().max(100).optional(),
  /** Hosts opened with the project's own server certificate (no phone setup needed for them). */
  ownCertHosts: z.array(z.string().max(300)).max(50).optional(),
  /**
   * The phone's own background traffic (`isPhoneBackgroundHost`: Android, Google Play services, the phone maker, crash
   * reporting) the proxy passed through without opening or recording it (agent 1.4.1+).
   */
  background: z.array(z.string().max(300)).max(50).optional(),
});
export type RecordingTraffic = z.infer<typeof recordingTrafficSchema>;

/**
 * Domains (the domain itself and every host below it) the phone talks to on its own while recording: the proxy is set
 * for the whole phone, so Android, Google Play services, the phone maker's services and SDKs for crash reporting and
 * analytics go through it too. They never trust a user-installed certificate and never belong in a performance script.
 * Kept narrow on purpose: googleapis.com itself is NOT here, because Firebase apps use it as their own backend
 * (firestore, identitytoolkit, ...); only its Android/Firebase housekeeping hosts are.
 */
export const PHONE_BACKGROUND_DOMAINS: readonly string[] = [
  // Android and Google services
  'google.com',
  'android.com',
  'gstatic.com',
  'googleusercontent.com',
  'gvt1.com',
  'gvt2.com',
  'ggpht.com',
  'play.googleapis.com',
  'play-fe.googleapis.com',
  'android.googleapis.com',
  'androidcheck-pa.googleapis.com',
  'firebaseinstallations.googleapis.com',
  'firebaselogging-pa.googleapis.com',
  'firebaseremoteconfig.googleapis.com',
  'firebaseremoteconfigrealtime.googleapis.com',
  'crashlyticsreports-pa.googleapis.com',
  'fcmregistrations.googleapis.com',
  'mtalk.google.com',
  // Analytics, ads, crash reporting
  'app-measurement.com',
  'crashlytics.com',
  'doubleclick.net',
  'googlesyndication.com',
  'googleadservices.com',
  'appcenter.ms',
  // Phone makers
  'samsung.com',
  'samsungapps.com',
  'samsungcloud.com',
  'samsungosp.com',
  'samsungdm.com',
  'xiaomi.com',
  'miui.com',
  'hicloud.com',
  'oppomobile.com',
  'heytapmobi.com',
];

/** Whether `host` is the phone's own background traffic (see `PHONE_BACKGROUND_DOMAINS`), not the app's. */
export function isPhoneBackgroundHost(host: string): boolean {
  const h = host.toLowerCase().replace(/\.$/, '');
  return PHONE_BACKGROUND_DOMAINS.some((d) => h === d || h.endsWith(`.${d}`));
}

/** POST /agent/recordings/:id/events: what the agent saw since its last post. */
export const recordingEventsSchema = z.object({
  /** Agent-side status changes: recording (app launched), stopped (after Stop), failed (with error). */
  status: z.enum(['recording', 'stopped', 'failed']).optional(),
  error: z.string().max(2000).optional(),
  actions: z.array(mobileRawActionSchema).max(500).default([]),
  /** Texts on the screen right now (for "Add a check"), newest dump. */
  texts: z.array(z.string().max(300)).max(200).optional(),
  /** Screenshots of taps without a name (JPEG, base64, the touch marked), referenced by the taps' `shot`. */
  shots: z.array(z.object({ id: shotId, jpeg: z.string().max(700_000) })).max(20).default([]),
  /** Performance recordings: requests the app sent since the last post (the agent keeps each post below a few MB). */
  exchanges: z.array(recordedExchangeSchema).max(500).default([]),
  traffic: recordingTrafficSchema.optional(),
});
export type RecordingEvents = z.infer<typeof recordingEventsSchema>;

/**
 * What the project's AI model made of a tap's screenshot: the element's name as a step would say it ("the \"Home\" icon
 * in the bottom navigation bar"), or `keyboard` (a key on the on-screen keyboard: typing, not a step), or why not.
 */
export interface RecordingShotName {
  name?: string;
  keyboard?: boolean;
  error?: string;
}

/** Most taps per recording named by the AI model (each is one model call). */
export const RECORDING_MAX_AI_NAMES = 300;

/** The prompt sent with a tap's screenshot. */
export const SHOT_NAME_PROMPT = [
  'This is a screenshot of an Android app. The red ring marks where the user tapped.',
  'Name the element that was tapped so a tester can find it again, as a short phrase, for example:',
  'the "Log in" button / the "Home" icon in the bottom navigation bar / the cart icon at the top right / the "Settings" tab.',
  'Put the exact text the element shows in double quotes when it has text. Mention where it is only when that helps to tell it apart.',
  'If the ring is on the on-screen keyboard, reply KEYBOARD. If the screen is black or nothing can be seen there, reply UNKNOWN.',
  'Reply with the phrase only.',
].join('\n');

/** The model's reply -> a name for the step (one short line), keyboard, or nothing. */
export function shotNameFromReply(reply: string): RecordingShotName {
  const line = (reply.split('\n').map((l) => l.trim()).find(Boolean) ?? '').replace(/^["'`*]+|["'`*.]+$/g, '').trim();
  if (/^keyboard$/i.test(line)) return { keyboard: true };
  if (!line || /^unknown$/i.test(line)) return { error: 'The AI model could not tell what was tapped (the screen may be protected and show black).' };
  // A phrase about an element, not an essay or a refusal.
  if (line.length > 140 || /\b(sorry|cannot|can't|unable)\b/i.test(line)) return { error: 'The AI model gave no usable name.' };
  return { name: /^(the|a|an)\s/i.test(line) ? line.replace(/^(A|An|The)\s/, (m) => m.toLowerCase()) : `the ${line}` };
}

/** The answer to an events post: what the tester asked for in the web app. */
export interface RecordingControl {
  stop: boolean;
  cancel: boolean;
}

/** POST /projects/:id/recordings */
export const recordingStartSchema = z.object({
  /** Device profile of an agent phone: agent:<agentId>:<serial>. Or `emulator`. */
  device: z.string().min(1).max(200).optional(),
  /**
   * Record on one of 1QA's emulators instead (performance scripts only): the Android device profile (AVD) of an
   * Android runner, e.g. "1qa-android-34". The tester uses the app on the emulator's screen in the browser.
   */
  emulator: z.string().regex(/^[A-Za-z0-9._-]{1,100}$/).optional(),
  /** Install this build first (a fresh install); null: the app already on the phone. */
  buildId: z.string().min(1).nullable().default(null),
  /**
   * Also record the app's network traffic for a performance script (agent 1.4.0+): the phone goes through the agent's
   * recording proxy while recording.
   */
  traffic: z.boolean().default(false),
  /**
   * Performance recordings: the performance project the script is for. Its server certificates go to the agent, so
   * the phone needs no 1QA certificate for those hosts.
   */
  perfProjectId: z.string().min(1).optional(),
});
export type RecordingStart = z.input<typeof recordingStartSchema>;

/**
 * POST /recordings/:id/input: what the tester does on an emulator's screen in the browser, in display pixels of the
 * emulator (the frames' size). The Android runner plays it on the emulator's touchscreen and keys, so the recording
 * sees it exactly like a finger on a phone.
 */
export const recordingInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('tap'), x: z.number().int().min(0).max(10_000), y: z.number().int().min(0).max(10_000), long: z.boolean().optional() }),
  z.object({
    kind: z.literal('swipe'),
    x0: z.number().int().min(0).max(10_000),
    y0: z.number().int().min(0).max(10_000),
    x1: z.number().int().min(0).max(10_000),
    y1: z.number().int().min(0).max(10_000),
    ms: z.number().int().min(50).max(5000),
  }),
  /** Typed into the field that has the focus. */
  z.object({ kind: z.literal('text'), text: z.string().min(1).max(500) }),
  z.object({ kind: z.literal('key'), key: z.enum(['back', 'home', 'enter', 'delete']) }),
]);
export type RecordingInput = z.infer<typeof recordingInputSchema>;

/** Redis list the API pushes emulator recordings to; Android runners take them (`EmulatorRecordingJob`). */
export const EMULATOR_RECORDING_JOBS = '1qa:rec:emulator:jobs';

/** An emulator recording for an Android runner (JSON in EMULATOR_RECORDING_JOBS). */
export interface EmulatorRecordingJob {
  recordingId: string;
  /** The Android device profile (AVD) to record on. */
  profile: string;
  appId: string;
  /** The build to install first (a fresh install); null: the app must be on the emulator already (it never is). */
  build: { id: string; sha256: string; storageKey: string; fileName: string; label: string } | null;
  /** The performance project's own server certificates (pinned apps). */
  serverCerts: ServerCertForAgent[];
  /** The job is dropped after this (epoch ms): nobody waits for it any more. */
  expiresAt: number;
}

/** Emulator recordings: a frame of the emulator's screen (GET /recordings/:id/screen, server-sent events). */
export interface RecordingFrame {
  /** Base64 PNG, no data: prefix. */
  png: string;
  ts: number;
}

/** GET /recordings/:id?after=n */
export interface RecordingView {
  id: string;
  projectId: string;
  status: RecordingStatus;
  error: string | null;
  /** "Pixel 7 · Android 15" */
  deviceLabel: string;
  appId: string;
  /** Raw actions from index `after` on; `next` is the index to ask from next time. */
  actions: MobileRawAction[];
  next: number;
  texts: string[];
  /** AI names of taps without a name, by screenshot id (all so far; a missing id is still being named). */
  names: Record<string, RecordingShotName>;
  startedAt: string;
  /** Performance recordings: the recording proxy's state; null when traffic is not recorded. */
  traffic: RecordingTraffic | null;
  /** Where it records: a tester's phone (Device Agent) or one of 1QA's emulators (screen in the browser). */
  target: 'phone' | 'emulator';
}

/** Redis keys of a recording session (API only; the agent reaches them through its routes). */
export const recordingKeys = {
  /** JSON RecordingSession. */
  session: (id: string) => `1qa:rec:${id}`,
  /** JSON { status, error }. */
  status: (id: string) => `1qa:rec:${id}:status`,
  /** List of JSON MobileRawAction. */
  actions: (id: string) => `1qa:rec:${id}:actions`,
  texts: (id: string) => `1qa:rec:${id}:texts`,
  /** A tap's screenshot (base64 JPEG). */
  shot: (id: string, shot: string) => `1qa:rec:${id}:shot:${shot}`,
  /** Hash: screenshot id -> JSON RecordingShotName. */
  names: (id: string) => `1qa:rec:${id}:names`,
  /** Counter of AI model calls (RECORDING_MAX_AI_NAMES). */
  aiCalls: (id: string) => `1qa:rec:${id}:ai`,
  /** "stop" or "cancel", set by the web app, read by the agent's next post. */
  control: (id: string) => `1qa:rec:${id}:control`,
  /** Performance recordings: list of JSON Exchange (recorded traffic), and its total size in bytes. */
  traffic: (id: string) => `1qa:rec:${id}:traffic`,
  trafficBytes: (id: string) => `1qa:rec:${id}:traffic:bytes`,
  /** JSON RecordingTraffic (the agent's latest). */
  trafficState: (id: string) => `1qa:rec:${id}:traffic:state`,
  /** Emulator recordings: list of JSON RecordingEvents from the Android runner (the API stores them as the agent's posts). */
  inbound: (id: string) => `1qa:rec:${id}:inbound`,
  /** Emulator recordings: list of JSON RecordingInput for the Android runner. */
  input: (id: string) => `1qa:rec:${id}:input`,
  /** Emulator recordings: pub/sub channel of JSON RecordingFrame, and the latest frame. */
  screen: (id: string) => `1qa:rec:${id}:screen`,
  frame: (id: string) => `1qa:rec:${id}:frame`,
};

/** A session ends this long after the agent's last post (agent or phone gone). */
export const RECORDING_IDLE_TTL_S = 120;
/** And never lasts longer than this. */
export const RECORDING_MAX_S = 3600;
/** Raw actions kept per recording. */
export const RECORDING_MAX_ACTIONS = 2000;
/** Recorded requests kept per recording (MAX_EXCHANGES in @1qa/perf), and their size in all. */
export const RECORDING_MAX_EXCHANGES = 5000;
export const RECORDING_MAX_TRAFFIC_BYTES = 96 * 1024 * 1024;

/**
 * POST /recordings/:id/perf-script: a stopped performance recording becomes a performance script. The reviewed steps
 * (with the time each began, `MobileStep.ts`) name the requests' transactions; 1QA correlates the traffic with the
 * performance project's test data (the review has just saved the typed passwords there) and answers with the capture.
 */
export const recordingPerfScriptSchema = z.object({
  perfProjectId: z.string().min(1),
  name: z.string().trim().min(1).max(200),
  steps: z
    .array(z.object({ kind: z.enum(['open', 'act', 'assert', 'wait']), value: z.string().max(2000), ts: z.number().int().nonnegative().optional() }))
    .min(1)
    .max(RECORDING_MAX_ACTIONS),
  /** Backend requests only: leave out pages and front-end framework requests (default, as the browser recorder). */
  backendOnly: z.boolean().default(true),
});
export type RecordingPerfScriptInput = z.input<typeof recordingPerfScriptSchema>;
/** Owner value of the phone lock while recording (agentKeys.lock): tests wait for it like for another test. */
export const recordingLockOwner = (id: string) => `rec:${id}`;

// ------------------------------------------------------------------ steps

export interface MobileStep {
  id: string;
  kind: StepKind;
  value: string;
  /** The review should ask the tester to look at this step: unnamed element, or the screen dump was late. */
  check?: string;
  /** Screenshot of the tap (the review shows it). */
  shot?: string;
  /** When the action began (epoch ms): requests from then until the next step are this step's (performance scripts). */
  ts?: number;
}

/** A password typed during recording: the tester gives its value in the review; saved as test data {{key}}. */
export interface MobileSecret {
  key: string;
  /** Field name, for the review ("Password"). */
  label: string;
}

export interface MobileRecording {
  steps: MobileStep[];
  secrets: MobileSecret[];
}

const clean = (s: string | undefined, max = 60): string => {
  const t = (s ?? '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
};

/** Widget words at the end of resource ids that say nothing to a reader ("search_view_edit_text" -> "search"). */
const WIDGET_WORDS = new Set(['edit', 'text', 'view', 'et', 'txt', 'tv', 'src', 'layout', 'container', 'widget']);

/** "login_button" -> "login button", "btnSignIn" -> "btn sign in", "open_search_view_edit_text" -> "open search". */
export function humanizeResId(id: string): string {
  const words = id
    .replace(/^.*[:/]/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase()
    .split(/\s+/);
  while (words.length > 1 && WIDGET_WORDS.has(words[words.length - 1]!)) words.pop();
  return words.join(' ');
}

/** Human name of a phone element, best source first. Double quotes become single so steps stay readable. */
export function mobileElementName(el: UiElement): string {
  const own = [el.text, el.desc, el.innerText, el.hint].map((c) => clean(c)).find((c) => c.length > 0);
  const name = own ?? (el.resId ? clean(humanizeResId(el.resId)) : '');
  return name.replace(/"/g, "'");
}

/** The kind of control, in plain words, from its Android class. */
export function mobileElementKind(el: UiElement): string {
  const cls = el.cls.split('.').pop()!.toLowerCase();
  if (el.editable || /edittext|textfield|textinput|searchview/.test(cls)) return 'field';
  if (/switch/.test(cls)) return 'switch';
  if (/checkbox/.test(cls)) return 'checkbox';
  if (/radiobutton/.test(cls)) return 'option';
  if (/button/.test(cls)) return 'button';
  if (/spinner/.test(cls)) return 'dropdown';
  if (/^tab|tabview|tabitem/.test(cls)) return 'tab';
  if (/image/.test(cls)) return 'image';
  return '';
}

/** `the "Sign in" button`, `the "Email" field`, `"Settings"` (plain element with text). */
export function describeMobileTarget(el: UiElement): string {
  const name = mobileElementName(el);
  const kind = mobileElementKind(el);
  if (name && kind) return `the "${name}" ${kind}`;
  if (name) return `"${name}"`;
  return kind ? `the ${kind}` : 'the element';
}

/** "at the top right", "in the middle"… for touches on nothing with a name. */
export function screenPosition(x: number, y: number, screen: { w: number; h: number }): string {
  const col = x < screen.w / 3 ? 'left' : x > (screen.w * 2) / 3 ? 'right' : '';
  const row = y < screen.h / 3 ? 'top' : y > (screen.h * 2) / 3 ? 'bottom' : '';
  if (!row && !col) return 'in the middle of the screen';
  if (!row) return `on the ${col} of the screen`;
  return `at the ${row}${col ? ` ${col}` : ''} of the screen`;
}

/** "Password" -> PASSWORD, "e-mail address" -> E_MAIL_ADDRESS (1QA test data key rules). */
export function toVariableKey(label: string, fallback = 'SECRET'): string {
  let k = label.normalize('NFKD').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').toUpperCase();
  if (!/^[A-Z]/.test(k)) k = k ? `${fallback}_${k}` : fallback;
  return k.slice(0, 64);
}

const quote = (v: string) => `"${v.replace(/"/g, "'")}"`;

/**
 * Compiles the agent's raw actions (plus the web app's checks) into steps:
 *  - actions are taken in time order
 *  - a tap on a field followed by typing into it becomes the typing only; repeated typing keeps the final value
 *  - taps on checkboxes / switches become check / uncheck (from the state before the tap)
 *  - repeated scrolls in the same direction become one step
 *  - password fields become {{PASSWORD}} placeholders plus a MobileSecret (the value is asked for in the review)
 *  - taps without a name use the AI model's name from their screenshot (`names`); taps it saw on the keyboard go
 */
export function compileMobile(actions: MobileRawAction[], names: Record<string, RecordingShotName> = {}): MobileRecording {
  const sorted = [...actions].sort((a, b) => a.ts - b.ts);
  const list: MobileRawAction[] = [];
  for (const a of sorted) {
    const prev = list[list.length - 1];
    if (a.kind === 'type' && prev?.kind === 'type' && prev.el.key === a.el.key) {
      list[list.length - 1] = { ...a, ts: prev.ts };
      continue;
    }
    if (a.kind === 'type' && prev?.kind === 'tap' && !prev.long && prev.el?.key === a.el.key) {
      list[list.length - 1] = { ...a, ts: prev.ts };
      continue;
    }
    if (a.kind === 'scroll' && prev?.kind === 'scroll' && prev.direction === a.direction && prev.el?.key === a.el?.key) continue;
    if (a.kind === 'launch' && list.some((x) => x.kind === 'launch')) continue;
    list.push(a);
  }

  const steps: MobileStep[] = [];
  const secrets: MobileSecret[] = [];
  const keyFor = (label: string): string => {
    const same = secrets.find((s) => s.label === (label || 'Password'));
    if (same) return same.key;
    const base = toVariableKey(label || 'PASSWORD', 'PASSWORD');
    let key = base;
    for (let i = 2; secrets.some((s) => s.key === key); i++) key = `${base}_${i}`;
    secrets.push({ key, label: label || 'Password' });
    return key;
  };
  // Step ids follow the actions, so a step keeps its id when an earlier one drops out (a tap the AI model found on
  // the keyboard) and the review can update the steps the tester has not changed.
  let n = 0;
  let at = 0;
  const push = (kind: StepKind, value: string, check?: string, shot?: string) => steps.push({ id: `rec-${n}`, kind, value, ...(check ? { check } : {}), ...(shot ? { shot } : {}), ts: at });

  for (const a of list) {
    n++;
    at = a.ts;
    switch (a.kind) {
      case 'launch':
        push('open', '');
        break;
      case 'tap': {
        const verb = a.long ? 'long press' : 'tap';
        const stale = a.stale ? 'You were faster than 1QA could read the screen: check this is the element you touched.' : undefined;
        if (a.shot) {
          // No name, or the phone answered too late to trust it: the AI model's name from the screenshot wins.
          const ai = names[a.shot];
          if (ai?.keyboard) break;
          if (ai?.name) {
            push('act', `${verb} ${ai.name}`, 'Named by the AI model from the screenshot: check it is what you tapped.', a.shot);
            break;
          }
          const where = a.el ? `${verb} ${describeMobileTarget(a.el)}${mobileElementName(a.el) ? '' : ` ${screenPosition(a.x, a.y, a.screen)}`}` : `${verb} ${screenPosition(a.x, a.y, a.screen)}`;
          push('act', where, ai?.error ? `${ai.error} Check this step against the screenshot.` : 'Naming it from the screenshot…', a.shot);
          break;
        }
        if (!a.el) {
          push('act', `${verb} ${screenPosition(a.x, a.y, a.screen)}`, stale ?? 'Nothing with a name was touched here: describe what you tapped.');
          break;
        }
        const target = describeMobileTarget(a.el);
        const unnamed = mobileElementName(a.el) ? undefined : 'This element has no name on screen: describe it (for example "the cart icon").';
        if (!a.long && a.el.checkable && a.el.checked !== undefined) push('act', `${a.el.checked ? 'uncheck' : 'check'} ${target}`, stale ?? unnamed);
        else push('act', `${verb} ${target}${mobileElementName(a.el) ? '' : ` ${screenPosition(a.x, a.y, a.screen)}`}`, stale ?? unnamed);
        break;
      }
      case 'type': {
        const target = describeMobileTarget(a.el);
        if (a.el.password) push('act', `type {{${keyFor(mobileElementName(a.el))}}} into ${target}`);
        else if (a.value === '') push('act', `clear ${target}`);
        else push('act', `type ${quote(a.value)} into ${target}`);
        break;
      }
      case 'scroll':
        if (a.direction === 'left' || a.direction === 'right') push('act', `swipe ${a.direction}${a.el && mobileElementName(a.el) ? ` on ${describeMobileTarget(a.el)}` : ''}`);
        else push('act', `scroll ${a.direction}${a.el && mobileElementName(a.el) ? ` in ${describeMobileTarget(a.el)}` : ''}`);
        break;
      case 'back':
        push('act', 'go back');
        break;
      case 'assert':
        push('assert', `the screen shows ${quote(clean(a.text, 200))}`);
        break;
    }
  }
  return { steps, secrets };
}

/**
 * Gives each recorded password a key no existing test data uses (project or organization, case-insensitive), so a new
 * recording never reuses - or hides - another test's password: PASSWORD, PASSWORD_2… Placeholders are rewritten.
 */
export function assignFreeSecretKeys<S extends { value: string }>(
  steps: S[],
  secrets: { key: string; label: string }[],
  existingKeys: Iterable<string>,
): { steps: S[]; keys: Map<string, string> } {
  const taken = new Set([...existingKeys].map((k) => k.toUpperCase()));
  const keys = new Map<string, string>();
  for (const s of secrets) {
    const base = toVariableKey(s.label || 'PASSWORD', 'PASSWORD');
    let key = base;
    for (let i = 2; taken.has(key.toUpperCase()); i++) key = `${base.slice(0, 64 - String(i).length - 1)}_${i}`;
    taken.add(key.toUpperCase());
    keys.set(s.key, key);
  }
  // One pass, so a renamed key is never renamed again (PASSWORD -> PASSWORD_2 while PASSWORD_2 -> PASSWORD_3).
  const rewrite = (v: string) => v.replace(/\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g, (m, name: string) => (keys.has(name) ? `{{${keys.get(name)}}}` : m));
  return { steps: steps.map((st) => ({ ...st, value: rewrite(st.value) })), keys };
}
